<template>
  <div class="flex flex-col w-full justify-start md:pl-12 results-container">
    <h1 v-if="!simple">{{ filterTitle(pageConfig.title) }}</h1>
    <h2 v-if="!loading">
      {{ results.length > 0 ? "" : $t("wegow.filters.noResults") }}
    </h2>
    <div
      v-if="
        (pageConfig.type === 'concerts' ||
          pageConfig.type === 'festivals' ||
          pageConfig.type === 'both') &&
          route.query.weswap
      "
      class="flex flex-wrap justify-start"
    >
      <event-card
        v-for="event in weswapEvents.available_weswap"
        :key="event.id"
        class="m-2"
        :event="event"
      />
    </div>
    <div
      v-else-if="
        pageConfig.type === 'concerts' ||
          pageConfig.type === 'festivals' ||
          pageConfig.type === 'both'
      "
      class="flex flex-wrap justify-start"
    >
      <event-card
        v-for="event in results"
        :key="event.id"
        :event="event"
        type="main-small"
        img-height="65%"
        class="m-1"
        :open-external="true"
      />
    </div>
    <div
      v-else-if="pageConfig.type === 'artists'"
      class="flex flex-wrap justify-start"
    >
      <artist-card
        v-for="artist in results"
        :key="artist.id"
        :artist="artist"
        class="m-1"
        :open-external="true"
      />
    </div>
    <div
      v-else-if="pageConfig.type === 'users'"
      class="flex flex-wrap justify-start"
    >
      <profile-card
        v-for="user in results"
        :key="user.id"
        :profile="user"
        class="m-1"
      />
    </div>
    <div
      v-else-if="pageConfig.type === 'venues'"
      class="flex flex-wrap justify-start"
    >
      <venue-card
        v-for="venue in results"
        :key="venue.id"
        :venue="venue"
        class="m-1"
        :open-external="true"
      />
    </div>
    <div
      v-else-if="pageConfig.type === 'pages'"
      class="flex flex-wrap justify-start"
    >
      <company-card
        v-for="company in results"
        :key="company.id"
        :company="company"
        class="m-1"
        :open-external="true"
      />
    </div>
    <filters-pagination
      v-if="currentPage > 1 || nextPage > 1"
      class="flex flex-row justify-center"
      style="width: 100%;"
      :current-page="currentPage"
      :next-page="nextPage"
      :total-items="count"
      :option-per-page-selected="24"
      @on-change-page="changePage($event)"
    />
    <div v-if="loading" class="mt-4">
      <wegow-loader class="min-h-screen" />
    </div>
  </div>
</template>

<script setup>
import { useFilter } from "@/composables/useFilter";
import EventCard from "@/components/wegow/ui/cards/EventCard";
import ArtistCard from "@/components/wegow/ui/cards/ArtistCard";
import VenueCard from "@/components/wegow/ui/cards/VenueCard";
import CompanyCard from "@/components/wegow/ui/cards/CompanyCard";
import FiltersPagination from "@/components/general/ui/elements/FiltersPagination";
import WegowLoader from "@/components/general/ui/WegowLoader";
import { useRoute, useRouter } from "vue-router";
import { useUser } from "@/composables/useUser";
import { useI18n } from "vue-i18n";
import { onMounted, computed, ref } from "vue";
import geoApi from "@/api/GeoApi";
import { VueCookieNext } from "vue-cookie-next";
import ProfileCard from "@/components/wegow/ui/cards/ProfileCard.vue";

const { weswapEvents } = useUser();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const {
  results,
  pageConfig,
  loading,
  currentPage,
  nextPage,
  count,
  changePage
} = useFilter();

// Mapeo de códigos de idioma a rutas de eventos por país
const countryEventRoutes = {
  es: "/es/conciertos/geo/espana?query=&type=both",
  de: "/de/conciertos/geo/deutschland?query=&type=both",
  gb: "/gb/conciertos/geo/great-britain?query=&type=both",
  fr: "/fr/conciertos/geo/france?query=&type=both",
  it: "/it/conciertos/geo/italia?query=&type=both",
  pt: "/pt/conciertos/geo/portugal?query=&type=both",
  ch: "/ch/conciertos/geo/die-schweiz?query=&type=both",
  nl: "/nl/conciertos/geo/netherlands?query=&type=both",
  ie: "/ie/conciertos/geo/ireland?query=&type=both",
  at: "/at/conciertos/geo/osterreich?query=&type=both",
  be: "/be/conciertos/geo/belgique?query=&type=both",
  mx: "/mx/conciertos/geo/mexico?query=&type=both",
  co: "/co/conciertos/geo/colombia?query=&type=both",
  ar: "/ar/conciertos/geo/argentina?query=&type=both",
  cl: "/cl/conciertos/geo/chile?query=&type=both",
  br: "/br/conciertos/geo/brasil?query=&type=both",
  us: "/us/conciertos/geo/united-states?query=&type=both",
  ca: "/ca/conciertos/geo/canada?query=&type=both",
  au: "/au/conciertos/geo/australia?query=&type=both",
  se: "/se/conciertos/geo/sweden?query=&type=both"
};

// Función para verificar si hay cookies de autenticación (simulada)
const hasAuthCookies = () => {
  const userRegionCookie = document.cookie
    .split("; ")
    .find(row => row.startsWith("wegow.user-region="));

  return !!userRegionCookie; // Devuelve true si la cookie existe
};

// Función para redirigir si no hay cookies
const redirectToCountryEvents = async () => {
  // Obtener el valor de la cookie 'wegow.user-region'
  const userRegionCookie = document.cookie
    .split("; ")
    .find(row => row.startsWith("wegow.user-region="))
    ?.split("=")[1]; // Extraer el valor de la cookie

  // Normalizar la ruta eliminando barras iniciales y finales
  const normalizedPath = route.path.replace(/^\/+|\/+$/g, "");
  const pathSegments = normalizedPath.split("/").filter(segment => segment);
  const langCode = pathSegments[0] || ""; // Por ejemplo, 'it' de '/it/'

  // Verificar si el código de idioma está soportado
  const isSupportedLang = countryEventRoutes[langCode];

  // Redirigir según la cookie o el idioma en la ruta
  if (userRegionCookie && countryEventRoutes[userRegionCookie]) {
    if (!sessionStorage.getItem("redirected")) {
      router.push(countryEventRoutes[userRegionCookie]);
      sessionStorage.setItem("redirected", "true");
      window.location.reload();
    }
    } else if (!userRegionCookie) {
    const ipRegion = await geoApi.getRegion();
    if (ipRegion && countryEventRoutes[ipRegion]) {
      cookie.setCookie("wegow.user-region", ipRegion, { path: "/" });
      if (!sessionStorage.getItem("redirected")) {
        router.push(countryEventRoutes[ipRegion]);
        sessionStorage.setItem("redirected", "true");
      }
    } else if (isSupportedLang && !hasAuthCookies()) {
      if (!sessionStorage.getItem("redirected")) {
        router.push(countryEventRoutes[langCode]);
        sessionStorage.setItem("redirected", "true");
      }
    }
  } else if (isSupportedLang && !hasAuthCookies()) {
    if (!sessionStorage.getItem("redirected")) {
      router.push(countryEventRoutes[langCode]);
      sessionStorage.setItem("redirected", "true");
    }
  }
};

// Ejecutar redirección al montar el componente
onMounted(async () => {
  await redirectToCountryEvents();
});

const filterTitle = title => {
  const routeParamConditionCompany =
    route.params.type === t("wegow.profile.sections.pages.slug");
  const routeQueryConditionCompany = route.query.type === "pages";
  const routeParamConditionVenue =
    route.params.type === t("wegow.profile.sections.venues.slug");
  const routeQueryConditionVenue = route.query.type === "venues";
  const routeParamConditionArtists =
    route.params.type === t("wegow.filters.artists").toLowerCase();
  const routeQueryConditionArtists = route.query.type === "artists";
  const routeParamConditionFestivals =
    route.params.type === t("wegow.filters.festivals").toLowerCase();
  const routeQueryConditionFestivals = route.query.type === "festivals";
  const routeParamConditionConcert =
    route.params.type === t("wegow.profile.sections.concert.slug");
  const routeQueryConditionConcert =
    route.query.type === "concerts" ||
    route.params.type === t("wegow.filters.concerts").toLowerCase();
  const routeQueryConditionPeople = route.query.type === "users";
  const routeParamConditionPeople =
    route.params.type === t("wegow.filters.people").toLowerCase();

  const titleformat = formatTitleYears(title);

  if ((routeParamConditionCompany || routeQueryConditionCompany) && title) {
    return titleformat?.replace(
      t("wegow.filters.concerts"),
      t("wegow.profile.sections.pages.filterTitle")
    );
  } else if ((routeParamConditionVenue || routeQueryConditionVenue) && title) {
    return titleformat?.replace(
      t("wegow.filters.concerts"),
      t("wegow.profile.sections.venues.title")
    );
  } else if (
    (routeParamConditionArtists || routeQueryConditionArtists) &&
    title
  ) {
    return titleformat?.replace(
      t("wegow.filters.concerts"),
      t("wegow.filters.artists")
    );
  } else if (
    (routeQueryConditionFestivals || routeParamConditionFestivals) &&
    title
  ) {
    return titleformat?.replace(
      t("wegow.filters.concerts"),
      t("wegow.filters.festivals")
    );
  } else if (route.query.type === "both" && title) {
    return titleformat?.replace(
      t("wegow.filters.concerts"),
      t("wegow.filters.both")
    );
  } else if (routeQueryConditionPeople || routeParamConditionPeople) {
    return t("wegow.filters.userWegow");
  } else {
    return titleformat;
  }
};

const formatTitleYears = title => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const nextYear = currentYear + 1;
  const month = currentDate.getMonth();

  if (month > 9) {
    title = title?.replace(new RegExp(`\\b${currentYear}\\b`, "g"), "").trim();
  } else {
    title = title?.replace(new RegExp(`\\b${nextYear}\\b`, "g"), "").trim();
  }
  return title?.replace(/\s{2,}/g, " ");
};

const props = defineProps({
  simple: {
    type: Boolean,
    default: false
  }
});
</script>

<style lang="scss" scoped>
.results-container {
  max-width: 1400px;
  h1 {
    font-weight: 400;
    font-size: 25px;
    line-height: 30px;
    margin-bottom: 40px;
  }
  h2 {
    font-weight: 400;
    font-size: 25px;
    line-height: 30px;
    color: $topaz;
  }
}
</style>
